
import type { Inventory, Vitals } from "../net/connection";
import type { CombatFeed } from "../client/combat";
import type { RenderFrame } from "./entities";

/** The slice of `main.ts`'s `Session` the render frame is built from. Every
 *  field is a straight read; nothing here calls back into the session. */
export interface FrameSession {
  readonly selfId: number;
  readonly displayName: string;
  readonly tick: number;
  readonly selfVitals: Vitals;
  readonly combat: CombatFeed;
  readonly bag: Inventory;
  readonly selfRace: number;
  readonly selfMount: number;
  readonly selfFlags: number;
  readonly selfAffectFlags: number;
  readonly selfGmLevel: number;
  readonly stallSigns: ReadonlyMap<number, string>;
  /** The local player's guild name, empty when they are in none. Optional so
   *  an embedding written before the guild tail existed still satisfies this
   *  slice; the tail is simply not drawn for it. */
  readonly guildName?: string;
  readonly guildNames?: ReadonlyMap<number, string>;
  /** The local player's guild id, 0 in none. */
  readonly guildId?: number;
  /** Every guild's sign the server has served this connection, by guild id:
   *  768 samples of red, green, blue and alpha. */
  readonly guildEmblems?: ReadonlyMap<number, readonly number[]>;
  readonly guildSymbols?: ReadonlyMap<number, CanvasImageSource>;
  /** Each ground item in view by entity id: its vnum and when it was told. */
  readonly groundItems?: ReadonlyMap<number, { readonly vnum: number; readonly seenMs: number }>;
}

/** The per-frame locals the loop computes just before the draw. Picked from
 *  `RenderFrame` so a field can never disagree with the frame it becomes. */
export type FrameInputs = Pick<
  RenderFrame,
  | "player"
  | "others"
  | "targetId"
  | "gestures"
  | "motions"
  | "playerMotionMode"
  | "anglers"
  | "alwaysShowNames"
  | "showNameHeld"
  | "showDamage"
  | "showSalesText"
  | "nowMs"
  | "dtMs"
  | "sitingGhost"
  | "cameraAnchor"
>;

/** Assemble the one `RenderFrame` the renderer is handed this tick. Pure: it
 *  reads its two arguments and allocates one object, exactly as the inline
 *  literal did. */
export function buildRenderFrame(session: FrameSession, inputs: FrameInputs): RenderFrame {
  return {
    player: inputs.player,
    playerId: session.selfId,
    playerName: session.displayName,
    serverTick: session.tick,
    playerVitals: session.selfVitals,
    others: inputs.others,
    targetId: inputs.targetId,
    combat: session.combat,
    gestures: inputs.gestures,
    motions: inputs.motions,
    playerMotionMode: inputs.playerMotionMode,
    anglers: inputs.anglers,
    playerWorn: session.bag.worn,
    playerRace: session.selfRace,
    playerFlags: session.selfFlags,
    playerAffectFlags: session.selfAffectFlags,
    playerGmLevel: session.selfGmLevel,
    playerGuildName: session.guildName ?? "",
    guildNames: session.guildNames,
    playerGuildId: session.guildId ?? 0,
    guildEmblems: session.guildEmblems,
    guildSymbols: session.guildSymbols,
    groundItems: session.groundItems,
    alwaysShowNames: inputs.alwaysShowNames,
    showNameHeld: inputs.showNameHeld,
    showDamage: inputs.showDamage,
    showSalesText: inputs.showSalesText,
    stallSigns: session.stallSigns,
    playerMount: session.selfMount,
    nowMs: inputs.nowMs,
    dtMs: inputs.dtMs,
    sitingGhost: inputs.sitingGhost,
    cameraAnchor: inputs.cameraAnchor,
  };
}
